import { brandAssetRepository } from '@vivaporta/brand';
import { applicationPath, resolveApplicationPath, type WorkAreaLocale } from '@vivaporta/work-areas';
import heroGastro from '../../img/hero/hero-gastro.png';
import heroHandel from '../../img/hero/hero-handel.png';
import heroHandwerk from '../../img/hero/hero-handwerk.png';
import heroPflege from '../../img/hero/hero-pflege.png';
import heroUnternehmenBedarf from '../../img/hero/hero-unternehmen-bedarf.png';
import heroUnternehmenBranchen from '../../img/hero/hero-unternehmen-branchen.png';
import heroUnternehmenDokumente from '../../img/hero/hero-unternehmen-dokumente.png';
import heroUnternehmenIntegration from '../../img/hero/hero-unternehmen-integration.png';
import globusCampaignBakery from '../../img/globus-campaign/bäckerei6.webp';
import globusCampaignBakeryFeature from '../../img/globus-campaign/bäckerei4.webp';
import globusCampaignButcher from '../../img/globus-campaign/metzger1.webp';
import globusCampaignButcherFeature from '../../img/globus-campaign/metzger3.webp';
import globusCampaignFish from '../../img/globus-campaign/Fisch 2.webp';
import globusCampaignGastronomy from '../../img/globus-campaign/Gastro.webp';
import globusCampaignMarkthalle from '../../img/globus-campaign/globus_markthalle.jpg';
import industryCare from '../../img/hero/industry-care-v2.png';
import industryCraft from '../../img/hero/industry-craft-v2.png';
import industryGastro from '../../img/hero/industry-gastro-v2.png';
import industryRetail from '../../img/hero/industry-retail-v2.png';
import nursePortrait1 from '../../img/hero/nurse-portrait-1.png';
import nursePortrait2 from '../../img/hero/nurse-portrait-2.png';
import nursePortrait3 from '../../img/hero/nurse-portrait-3.png';
import partnerEmek from '../../img/partner/emek.webp';
import partnerGlobus from '../../img/partner/globus.svg';
import partnerTempusleben from '../../img/partner/tempusleben.png';
import groupPhoto from '../../img/rnd/gruppenfoto.jpeg';
import teamKirstin from '../../img/team/Dr. Kirstin Wingler.png';
import teamEnes from '../../img/team/Enes Vuval.png';
import teamErgun from '../../img/team/Ergün Yalcin.png';
import teamGerdSchneider from '../../img/team/Gerd Schneider.png';
import teamMurat from '../../img/team/Mural Vuval.png';
import teamSerife from '../../img/team/Serife Vural-Banik.png';
import teamWilhelm from '../../img/team/Wilhelm Josten.png';

export const assets = {
  nursePortrait1,
  nursePortrait2,
  nursePortrait3,
  hero: 'https://www-static.vivaporta-medical.de/wp-content/uploads/2025/08/ChatGPT-Image-23.-Aug.-2025-15_17_53-1024x683.png?media=1756376448',
  teamEnes,
  teamWilhelm,
  teamSerife,
  teamMurat,
  teamKirstin,
  teamErgun,
  partner:
    'https://www-static.vivaporta-medical.de/wp-content/uploads/2025/07/tempusleben_Intensiv_4C_transparent-768x160.png?media=1756376448',
  partnerSecondary: 'https://www-static.vivaporta-medical.de/wp-content/uploads/2025/07/Bild-5.jpeg?media=1756376448',
  article:
    'https://usercontent.one/wp/www.vivaporta.org/wp-content/uploads/2025/07/Almanyada-Hemsirelik-1024x683.png?media=1732556520',
  globusCampaignMarkthalle,
  globusCampaignBakery,
  globusCampaignBakeryFeature,
  globusCampaignButcher,
  globusCampaignButcherFeature,
  globusCampaignFish,
  globusCampaignGastronomy,
  groupPhoto,
};

export { groupPhoto };

// Das Bewerberportal ist eine eigene Plattform-App auf einer ANDEREN Subdomain als die Landingpage.
// Ein relativer Pfad (/basvuru-formu) würde daher auf der Landing-Domain landen → 404. Wir leiten die
// Basis-URL deshalb zur Laufzeit aus dem aktuellen Host ab. Auf der echten Produktionsdomain
// (*.vivaporta.org) liegt das Formular unter app.vivaporta.org; auf Adornis-Umgebungen
// bleibt die bisherige landing → platform Ableitung erhalten.
const PORTAL_PRODUCTION_BASE = 'https://app.vivaporta.org';
const ADORNIS_PLATFORM_PRODUCTION_BASE = 'https://viva-porta-platform.production4.adornis.de';

const resolveAdornisPortalHost = (hostname: string): string | null => {
  const runtimeHost = hostname
    .replace('-platform-page-runtime.', '-platform-runtime.')
    .replace('-landing-page-runtime.', '-platform-runtime.')
    .replace('-landing-runtime.', '-platform-runtime.');
  if (runtimeHost !== hostname) return runtimeHost;

  const platformHost = hostname
    .replace('-landing-page.', '-platform.')
    .replace('-landing.', '-platform.')
    .replace('landing', 'platform');
  return platformHost !== hostname ? platformHost : null;
};

// Wird auch außerhalb dieses Moduls für Plattform-Links im Editor genutzt, damit sie exakt derselben
// Host-Ableitung folgen wie die Anmeldebögen – beide liegen auf der Plattform-App.
export const resolveApplicationPortalBase = (): string => {
  // The SSR runtime exposes a lightweight `window` shim backed by globalThis,
  // but it intentionally has no Location object. Treat that exactly like a
  // normal non-browser environment so shared defaults remain server-safe.
  if (typeof window === 'undefined' || !window.location || !window.document?.defaultView) return PORTAL_PRODUCTION_BASE;
  const { hostname, protocol } = window.location;
  if (hostname === 'vivaporta.org' || hostname.endsWith('.vivaporta.org')) return PORTAL_PRODUCTION_BASE;
  const adornisPortalHost = resolveAdornisPortalHost(hostname);
  if (adornisPortalHost) return `${protocol}//${adornisPortalHost}`;
  return ADORNIS_PLATFORM_PRODUCTION_BASE;
};

/** Repair saved portal links for the active environment, preserving form paths and campaign parameters. */
export const resolveApplicationPortalHref = (href: string, portalBase = resolveApplicationPortalBase(), locale?: WorkAreaLocale): string => {
  try {
    const url = new URL(href, PORTAL_PRODUCTION_BASE);
    const route = resolveApplicationPath(url.pathname);
    if (!route) return href;
    const isKnownPortal = [
      PORTAL_PRODUCTION_BASE,
      ADORNIS_PLATFORM_PRODUCTION_BASE,
      'https://www.vivaporta.org',
      'https://vivaporta.org',
    ].includes(url.origin);
    const isAdornisApp = url.hostname.endsWith('.adornis.de') &&
      (url.hostname.includes('-platform') || resolveAdornisPortalHost(url.hostname) !== null);
    if (!isKnownPortal && !isAdornisApp) return href;
    const requestedLocale = locale ?? url.searchParams.get('lang') ?? route.locale;
    const targetLocale = requestedLocale === 'en' || requestedLocale === 'tr' ? requestedLocale : 'de';
    url.pathname = applicationPath(route.key, targetLocale);
    if (locale || url.searchParams.has('lang')) url.searchParams.set('lang', targetLocale);
    return `${portalBase}${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};

export type GlobusProfession = 'bakery' | 'butcher' | 'fresh-counter' | 'gastronomy';

// overview = Branchenauswahl, danach pro Branche der direkte Anmeldebogen.
export const applicationPortal = {
  get overview() {
    return `${resolveApplicationPortalBase()}/basvuru-formu`;
  },
  get pflege() {
    return `${resolveApplicationPortalBase()}/basvuru-formu/pflege`;
  },
  get gastro() {
    return `${resolveApplicationPortalBase()}/basvuru-formu/gastro`;
  },
  get handwerk() {
    return `${resolveApplicationPortalBase()}/basvuru-formu/handwerk`;
  },
  get einzelhandel() {
    return `${resolveApplicationPortalBase()}/basvuru-formu/einzelhandel`;
  },
  get metzger() {
    return `${resolveApplicationPortalBase()}/basvuru-formu/metzger`;
  },
  globusProfession(profession: GlobusProfession) {
    const url = new URL(`${resolveApplicationPortalBase()}/basvuru-formu/einzelhandel`);
    url.searchParams.set('profession', profession);
    return url.toString();
  },
  einzelhandelFocus(focus: 'baeckerei' | 'frischetheke') {
    const url = new URL(`${resolveApplicationPortalBase()}/basvuru-formu/einzelhandel`);
    url.searchParams.set('focus', focus);
    return url.toString();
  },
} as const;

export const contact = {
  // Alle "Anmelden/Bewerben"-CTAs der Startseite führen ins Anmeldeportal zur Branchenauswahl.
  application: applicationPortal.overview,
  medical: 'https://www.vivaporta-medical.de/kontakt/',
  mail: 'mailto:info@vivaporta.org',
};

export {
  brandAssetRepository,
  heroGastro,
  heroHandel,
  heroHandwerk,
  heroPflege,
  heroUnternehmenBedarf,
  heroUnternehmenBranchen,
  heroUnternehmenDokumente,
  heroUnternehmenIntegration,
  industryCare,
  industryCraft,
  industryGastro,
  industryRetail,
  nursePortrait1,
  nursePortrait2,
  nursePortrait3,
  partnerEmek,
  partnerGlobus,
  partnerTempusleben,
  teamEnes,
  teamErgun,
  teamGerdSchneider,
  teamKirstin,
  teamMurat,
  teamSerife,
  teamWilhelm,
};
